import type { InputState } from './types';
import { GameRenderer } from './GameRenderer';
import { applyRelativeTouchDrag } from './utils';

export interface InputCallbacks {
  canDrop: () => boolean;
  onDrop: (worldX: number) => void;
  onInteraction: () => void;
}

export class InputController {
  state: InputState = {
    pointerId: null,
    dragging: false,
    latestClientX: 0,
    previewWorldX: 180,
  };
  private consumedClientX = 0;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly renderer: GameRenderer,
    private readonly callbacks: InputCallbacks,
  ) {
    canvas.addEventListener('pointerdown', this.onPointerDown);
    canvas.addEventListener('pointermove', this.onPointerMove);
    canvas.addEventListener('pointerup', this.onPointerUp);
    canvas.addEventListener('pointercancel', this.onPointerCancel);
    canvas.addEventListener('contextmenu', (event) => event.preventDefault());
  }

  destroy(): void {
    this.canvas.removeEventListener('pointerdown', this.onPointerDown);
    this.canvas.removeEventListener('pointermove', this.onPointerMove);
    this.canvas.removeEventListener('pointerup', this.onPointerUp);
    this.canvas.removeEventListener('pointercancel', this.onPointerCancel);
  }

  private onPointerDown = (event: PointerEvent): void => {
    if (this.state.pointerId !== null) return;
    if (!this.callbacks.canDrop()) return;
    this.state.pointerId = event.pointerId;
    this.state.dragging = true;
    this.state.latestClientX = event.clientX;
    this.consumedClientX = event.clientX;
    this.canvas.setPointerCapture(event.pointerId);
    this.callbacks.onInteraction();
  };

  private onPointerMove = (event: PointerEvent): void => {
    if (event.pointerId !== this.state.pointerId || !this.state.dragging) return;
    this.state.latestClientX = event.clientX;
  };

  private onPointerUp = (event: PointerEvent): void => {
    if (event.pointerId !== this.state.pointerId) return;
    this.state.latestClientX = event.clientX;
    this.updateFrame();
    if (this.state.dragging) {
      this.callbacks.onDrop(this.state.previewWorldX);
    }
    this.releasePointer(event.pointerId);
  };

  private onPointerCancel = (event: PointerEvent): void => {
    if (event.pointerId !== this.state.pointerId) return;
    this.releasePointer(event.pointerId);
  };

  private releasePointer(pointerId: number): void {
    if (this.canvas.hasPointerCapture(pointerId)) this.canvas.releasePointerCapture(pointerId);
    this.state.pointerId = null;
    this.state.dragging = false;
  }

  updateFrame(): void {
    if (!this.state.dragging) return;
    const deltaClientX = this.state.latestClientX - this.consumedClientX;
    this.consumedClientX = this.state.latestClientX;
    this.state.previewWorldX = applyRelativeTouchDrag(
      this.state.previewWorldX,
      deltaClientX,
      this.renderer.scale,
    );
    this.renderer.previewX = this.state.previewWorldX;
  }
}
